import {
  getCoreAcademicNamespace,
  getPermissionUtils,
  getPersonalSettings,
  getUser,
  TenantType,
} from '@tyro/api';
import { LoadingScreen } from '@tyro/core/lazy-loader';
import { type ReactNode, useEffect, useState } from 'react';
import { Outlet, type RouteObject, redirect } from 'react-router';
import type { NavCategory } from '../@types/nav-object';
import { registerAppRoutes } from '../app-routes-registry';
import { ErrorElement } from '../components/error-element';
import { buildPathsWithAccess } from '../utils/build-paths-with-access';
import { buildRouteTree } from '../utils/build-route-tree';
import { isChunkLoadError } from '../utils/is-chunk-load-error';
import { sentryCreateBrowserRouter, setSentryUser } from '../utils/sentry';

export interface AppRouterProps {
  getNavCategories: () => Promise<NavCategory[]>;
  Shell: React.FC<{ children: ReactNode }>;
  additionalRoutes: RouteObject[];
}

export function useAppRouter({
  getNavCategories,
  Shell,
  additionalRoutes,
}: AppRouterProps) {
  const [router, setRouter] = useState<Awaited<
    ReturnType<typeof sentryCreateBrowserRouter>
  > | null>(null);

  const createRouter = async () => {
    // Prefetch core data in parallel with route module loading.
    // These use queryClient.fetchQuery so later calls get the cached result.
    getUser();
    getPersonalSettings();
    getCoreAcademicNamespace();

    const navCategories = await getNavCategories();
    const fullPathsWithAccess = await buildPathsWithAccess(navCategories);
    const routeTree = buildRouteTree(navCategories, fullPathsWithAccess);
    registerAppRoutes(routeTree);

    setRouter(
      await sentryCreateBrowserRouter([
        {
          loader: async () => {
            try {
              const responses = await Promise.all([
                getUser(),
                getCoreAcademicNamespace(),
              ]);
              const [user] = responses;

              if (user.activeProfile?.blocked) {
                throw new Response('Forbidden', { status: 403 });
              }

              setSentryUser(user.activeProfile);

              return responses;
            } catch (error) {
              if (
                error instanceof Error &&
                error?.message === 'Failed to fetch'
              ) {
                throw new Response('Service Unavailable', { status: 503 });
              }

              throw error;
            }
          },
          element: (
            <Shell>
              <Outlet />
            </Shell>
          ),
          errorElement: <ErrorElement Shell={Shell} />,
          HydrateFallback: LoadingScreen,
          children: [
            {
              index: true,
              loader: async () => {
                const [permissions, { activeProfile }] = await Promise.all([
                  getPermissionUtils(),
                  getUser(),
                  getPersonalSettings(),
                ]);

                if (permissions.isStudent || permissions.isContact) {
                  return redirect('/people/students');
                }

                if (permissions.isTyroTenantAndUser) {
                  return redirect('/admin/schools');
                }

                if (
                  activeProfile?.tenant.tenantType === TenantType.Organization
                ) {
                  return redirect(
                    permissions.isTyroUser ? '/stats' : '/fees/payouts',
                  );
                }

                return redirect('/dashboard');
              },
            },
            ...routeTree,
          ],
        },
        ...additionalRoutes,
        {
          path: '*',
          loader: () => {
            throw new Response('Not Found', { status: 404 });
          },
          errorElement: <ErrorElement Shell={Shell} />,
        },
      ]),
    );
  };

  useEffect(() => {
    createRouter().catch((error) => {
      // A chunk error here means the one-time reload already happened and the
      // fresh build still can't load the chunk; retrying would refetch the same
      // dead URL, so surface it rather than loop.
      if (isChunkLoadError(error)) {
        throw error;
      }

      // Transient failure (e.g. dev server cold start) — retry once.
      setTimeout(() => createRouter(), 2000);
    });
  }, []);

  return router;
}
